<template>
  <nav class="nav">
    <PopoverRoot>
      <PopoverTrigger>
        <div class="nav__header">
          <img :src="getWorkspaceBranding(currentWorkspace?.name).logo" class="nav__header__logo" />
          <TextEllipsis class="nav__header__title">
            {{ currentWorkspace?.name }} ({{ getWorkspaceBranding(currentWorkspace?.name).flag }})
          </TextEllipsis>
          <Icon icon="lucide:chevron-down" :size="14" class="nav__header__chevron" />
        </div>
      </PopoverTrigger>

      <PopoverPortal>
        <PopoverContentWrapper side="bottom" :align-offset="8" :side-offset="-4" hide-when-detached>
          <div class="WorkspaceSwitcher">
            <div class="WorkspaceSwitcherSection" style="overflow: auto">
              <div
                v-for="workspace in workspaces"
                :key="workspace.id"
                class="WorkspaceSwitcherItem"
                @click="switchWorkspace(workspace)"
              >
                <img class="WorkspaceSwitcherItem__indicator" :src="getWorkspaceBranding(workspace.name).logo" />
                <TextEllipsis>{{ workspace.name }} ({{ getWorkspaceBranding(workspace.name).flag }})</TextEllipsis>
                <div v-if="currentWorkspace?.id === workspace.id" class="WorkspaceSwitcherItem__badge">
                  <Icon icon="lucide:check" :size="10" :stroke-width="3" />
                </div>
              </div>
            </div>

            <div class="WorkspaceSwitcherSection">
              <NuxtLink to="/settings/members">
                <div class="WorkspaceSwitcherItem">
                  <div class="WorkspaceSwitcherItem__indicator">
                    <Icon icon="lucide:users-round" />
                  </div>
                  <TextEllipsis>Gérer les membres</TextEllipsis>
                </div>
              </NuxtLink>

              <NuxtLink v-if="isManager" to="/settings/managees">
                <div class="WorkspaceSwitcherItem">
                  <div class="WorkspaceSwitcherItem__indicator">
                    <Icon :icon="mapIcon.user_pen" />
                  </div>
                  <TextEllipsis>Gérer mes managés</TextEllipsis>
                </div>
              </NuxtLink>
            </div>

            <div class="WorkspaceSwitcherSection">
              <NuxtLink to="/settings/account">
                <div v-if="teamMember" class="WorkspaceSwitcherItem">
                  <TeamMemberAvatar :team-member="teamMember" />
                  <TextEllipsis>{{ getFullName(teamMember) }}</TextEllipsis>
                </div>
              </NuxtLink>

              <NuxtLink to="/settings/workspace">
                <div class="WorkspaceSwitcherItem">
                  <div class="WorkspaceSwitcherItem__indicator">
                    <Icon icon="lucide:settings" />
                  </div>
                  <TextEllipsis>Paramètres du workspace</TextEllipsis>
                </div>
              </NuxtLink>
            </div>

            <div class="WorkspaceSwitcherSection">
              <div class="WorkspaceSwitcherItem" @click="navigateTo('/logout')">
                <div class="WorkspaceSwitcherItem__indicator">
                  <Icon icon="lucide:door-open" />
                </div>
                <TextEllipsis>Se déconnecter</TextEllipsis>
              </div>
            </div>
          </div>
        </PopoverContentWrapper>
      </PopoverPortal>
    </PopoverRoot>

    <div class="flex flex-col gap-1 my-2 px-3">
      <div class="flex items-center gap-2">
        <NavSearch class="min-w-0 flex-1" />

        <AwTooltip text="En cours d'appel ? Clique ici pour être redirigé vers la fiche du talent !">
          <AwButton
            class="shrink-0"
            size="md"
            icon="i-ph-phone"
            color="secondary"
            variant="outline"
            aria-label="Ouvrir l'appel en cours"
            :is-loading="isOpeningOngoingCall"
            @click="openOngoingCall"
          />
        </AwTooltip>
      </div>

      <NuxtLink v-slot="{ isActive }" to="/dashboard/action-center" style="margin-top: 8px">
        <NavButton
          class="NavButton"
          margin="0"
          title="Accueil"
          border-left="none"
          icon="i-ph-house-line"
          :is-active="isActive && !isNotificationSheetOpened && !isFromFavorites"
        />
      </NuxtLink>
      <div v-if="isTodoListEnabled" class="nav-tasks">
        <NuxtLink v-slot="{ isActive }" to="/tasks" class="nav-tasks__link">
          <NavButton
            class="NavButton"
            margin="0"
            :title="$t('tasks.nav.title')"
            border-left="none"
            icon="i-ph-check-square"
            :is-active="isActive && !isNotificationSheetOpened && !isFromFavorites"
          />
        </NuxtLink>
        <div class="nav-tasks__create">
          <AwDropdownMenu :content="{ side: 'right', align: 'start' }" :items="taskCreateMenuItems">
            <AwButton
              size="sm"
              color="gray"
              variant="ghost"
              icon="i-ph-plus"
              :aria-label="$t('tasks.nav.createAriaLabel')"
              @click.stop
            />
          </AwDropdownMenu>
        </div>
      </div>
      <TaskCreateModal
        v-if="isTodoListEnabled"
        v-model:open="isTaskCreateModalOpen"
        :action-type="selectedTaskAction"
      />
      <NavButton
        class="NavButton"
        icon="i-ph-bell"
        border-left="none"
        margin="0"
        :is-active="isNotificationSheetOpened"
        @click="isNotificationSheetOpened = true"
      >
        <TextEllipsis> Notification{{ countUnread > 1 ? "s" : "" }} </TextEllipsis>
        <NotificationBadge :count="countUnread" style="margin-left: auto" />
      </NavButton>
      <NuxtLink v-if="isConsultantKpiEnabled" v-slot="{ isActive }" to="/consultant-dashboard">
        <NavButton
          class="NavButton"
          margin="0"
          title="Performance"
          border-left="none"
          icon="i-ph-chart-line-up"
          :is-active="isActive"
          style="margin-bottom: 8px"
        />
      </NuxtLink>
      <LazyNotificationSheet v-if="isNotificationSheetOpened" @on-close="isNotificationSheetOpened = false" />

      <hr />
    </div>
    <div class="nav__content">
      <!-- Section Favoris -->
      <NavButtonGroup>
        <NavSection :section="favoritesSection">
          <template #empty>
            <div class="empty-favorite-container">
              <div>Marquer vos pages favorites</div>
              <div class="empty-favorite-icon-text">
                <AwIcon name="i-ph-star-fill" color="orange" />
                pour un accès rapide
              </div>
            </div>
          </template>
        </NavSection>
      </NavButtonGroup>

      <template v-for="section in navButtonSectionsFiltered" :key="section.name">
        <NavButtonGroup>
          <NavSection :section="section"></NavSection>
        </NavButtonGroup>
      </template>
    </div>

    <!-- DEVTOOLS -->
    <div v-if="isNotProduction" style="padding: 12px">
      <LazyDevToolsPanel />
    </div>
  </nav>
</template>

<script setup lang="ts">
import { useCall } from "~/components/call/composables/useCall";
import { useNotification } from "~/components/notification/composables/useNotification";
import { useManagees } from "~/components/team-member/composables/useManagees";
import { useUserMe } from "~/components/user/composables/useUser";
import { useWorkspaceMe } from "~/components/workspace/composables/useWorkspace";
import { useEnvironment } from "~/composables/useEnvironment";
import { useFeatureFlag } from "~/composables/useFeatureFlag";
import type { NavButton } from "~/composables/useNavBarData";
import { useToast } from "~/composables/useToast";
import { useNavbar } from "~/composables/useNavbar";
import type { Color } from "@asap/design/styles/colors";
import { useFavorites } from "~/composables/useFavorites";
import { useNavBarData } from "~/composables/useNavBarData";
import { getFullName } from "@asap/shared/utils/people";
import type { NavButtonCount } from "@asap/shared/types/navbar";
import type { AppRole } from "@asap/shared/utils/supabase.enum";
import { PopoverRoot, PopoverTrigger, PopoverPortal } from "radix-vue";
import logoAsapWork from "~/assets/images/logo-square-color.webp";
import logoMenco from "~/assets/images/logo-menco.png";
import logoMakeYourJob from "~/assets/images/logo-make-your-job.jpeg";
import logoJustConstruction from "~/assets/images/logo-just-construction.jpeg";
import { useTaskTrigger } from "~/components/task/composables/useTaskTrigger";

const workspaceBranding: Record<string, { logo: string; flag: string }> = {
  Menco: { logo: logoMenco, flag: "🇫🇷" },
  "Make Your Job": { logo: logoMakeYourJob, flag: "🇫🇷" },
  "Just Construction": { logo: logoJustConstruction, flag: "🇺🇸" },
};

function getWorkspaceBranding(name: string | undefined) {
  if (name && name in workspaceBranding) {
    return workspaceBranding[name];
  }
  return { logo: logoAsapWork, flag: "🇫🇷" };
}

const { isNotProduction } = useEnvironment();
const { isManager } = useManagees();
const { getNavbarCount } = useNavbar();
const { currentWorkspace } = useWorkspaceMe();
const { teamMember, workspaces, switchWorkspace } = useUserMe();

const route = useRoute();
const isFromFavorites = computed(() => route.query.favorite === "true");

const { countUnread } = useNotification();
const { navButtonSectionsFiltered } = useNavBarData();
const { favorites } = useFavorites({ withFavoriteCleanup: true });
const { getOngoingCallForMe } = useCall();
const { addToastError, addToastInfo } = useToast();
const { isFeatureFlagEnabled } = useFeatureFlag();
const isConsultantKpiEnabled = isFeatureFlagEnabled("consultant-kpi");
const isTodoListEnabled = isFeatureFlagEnabled("todo-list");

const isOpeningOngoingCall = ref(false);
const {
  isOpen: isTaskCreateModalOpen,
  selectedAction: selectedTaskAction,
  menuItems: taskCreateMenuItems,
} = useTaskTrigger();

const openOngoingCall = async () => {
  if (isOpeningOngoingCall.value) return;

  isOpeningOngoingCall.value = true;
  try {
    const ongoingCall = await getOngoingCallForMe();

    if (ongoingCall.talent) {
      await navigateTo(`/talents/${ongoingCall.talent.id}/notes`);
      return;
    }

    if (ongoingCall.hasOngoingCall) {
      addToastInfo({
        title: "L'appel en cours n'est lié à aucun talent",
      });
      return;
    }

    addToastInfo({
      title: "Tu n'as pas d'appel en cours",
    });
  } catch (error) {
    addToastError({ title: "Impossible de récupérer l'appel en cours." }, error);
  } finally {
    isOpeningOngoingCall.value = false;
  }
};

const navbarCount = ref<NavButtonCount>({});
const isNotificationSheetOpened = ref(false);

const favoritesSection = computed(() => ({
  name: "Favoris",
  icon: "i-ph-star-fill",
  color: "orange" as Color,
  items: favorites.value.map((fav) => ({
    to: `${fav.to}?favorite=true`,
    icon: fav.icon,
    label: fav.label,
    roles: [] as AppRole[],
  })),
}));

onMounted(async () => {
  try {
    navbarCount.value = await getNavbarCount();
  } catch (error) {
    console.error("Error fetching navbar count:", error);
  }
});
</script>

<style scoped lang="scss">
.nav {
  display: flex;
  flex-direction: column;
  width: 204px;
  min-width: 0;
  flex-shrink: 0;
  transition: var(--transition-2);
  overflow: hidden;

  &__header {
    display: flex;
    align-items: center;
    padding-inline: 16px;
    gap: 8px;
    height: 48px;
    cursor: pointer;
    transition: var(--transition-0);

    &:hover {
      background: var(--gray-7);
    }

    &:active {
      background: var(--gray-6);
    }

    &__logo {
      height: 20px;
      width: 20px;
      border-radius: 5px;
    }

    &__title {
      font-size: 14px;
      font-weight: 500;
    }

    &__chevron {
      margin-left: -4px;
      color: var(--gray-2);
      transition: var(--transition-1);

      :hover > & {
        transform: translateY(1px);
      }
    }
  }

  &__content {
    overflow-y: auto;
    display: flex;
    flex-direction: column;
    gap: 12px;
    padding-inline: 12px;
    flex: 1;
    padding-bottom: 45px;

    &::-webkit-scrollbar {
      display: none;
    }
  }

  &__buttons {
    display: flex;
    flex-direction: column;
    gap: 4px;
  }
}

.WorkspaceSwitcher {
  display: flex;
  flex-direction: column;
  border: 1px solid var(--gray-6);
  border-radius: 10px;
  box-shadow: var(--shadow-3);
  width: 288px;
  margin-left: 8px;
  background: var(--white);
  max-height: calc(var(--radix-popover-content-available-height) - 32px);
}

.WorkspaceSwitcherSection {
  display: flex;
  flex-direction: column;
  padding: 8px;

  &:not(:last-child) {
    border-bottom: 1px solid var(--gray-6);
  }
}

.nav-tasks {
  display: flex;
  align-items: center;
  position: relative;
  width: 100%;

  &__link {
    flex: 1;
    min-width: 0;
  }

  &__create {
    position: absolute;
    right: 2px;
  }
}

.empty-favorite-container {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  width: 90%;
  color: var(--gray-3);
  font-size: 11px;
  padding: 8px 0px;
  margin-left: 8px;
}

.empty-favorite-icon-text {
  display: flex;
  flex-direction: row;
  align-items: center;
  gap: 4px;
}

.WorkspaceSwitcherItem {
  display: flex;
  align-items: center;
  height: 32px;
  padding-inline: 8px;
  gap: 8px;
  border-radius: 5px;
  cursor: pointer;
  transition: var(--transition-0);
  flex-shrink: 0;

  &:hover {
    background: var(--gray-6);
  }

  &__indicator {
    display: flex;
    align-items: center;
    justify-content: center;
    height: 18px;
    width: 18px;
    border-radius: 5px;
  }

  &__badge {
    display: flex;
    align-items: center;
    justify-content: center;
    height: 16px;
    width: 16px;
    border-radius: 100px;
    background-color: var(--blue);
    margin-left: auto;
    color: var(--white);
    flex-shrink: 0;
  }
}
</style>
